import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChevronDownSmall } from '~/ui/core/icons';

import type { CommentsViewMode } from './active-thread-store';

type CommentsViewModeSelectorProps = {
    readonly activeCount: number;
    readonly hiddenCount: number;
    readonly value: CommentsViewMode;
    readonly onChange: (mode: CommentsViewMode) => void;
};

export const CommentsViewModeSelector: FunctionComponent<CommentsViewModeSelectorProps> = ({
    activeCount,
    hiddenCount,
    onChange,
    value,
}) => {
    const translate = useTranslate();
    const label =
        value === 'active'
            ? translate('pages.job-architecture-v2.comments.view-mode.active')
            : translate('pages.job-architecture-v2.comments.view-mode.hidden');
    const activeLabel = translate('pages.job-architecture-v2.comments.view-mode.active');
    const hiddenLabel = translate('pages.job-architecture-v2.comments.view-mode.hidden');
    const selectedCount = value === 'active' ? activeCount : hiddenCount;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button
                    aria-label={`${label}, ${selectedCount}`}
                    className="h-[30px] rounded-[10px] [border:1px_solid_var(--color-gray-300)]"
                    data-testid="comments-view-mode-selector"
                    iconPosition="trailing"
                    size="sm"
                    type="button"
                    variant="secondary"
                >
                    <span>{label}</span>
                    <span
                        className="flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-orange-100 px-xs text-[10px] font-semibold leading-none text-orange-800"
                        data-testid="comments-view-mode-count"
                    >
                        {selectedCount}
                    </span>
                    <IconChevronDownSmall />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuRadioGroup
                    value={value}
                    onValueChange={(nextValue) => {
                        if (nextValue === 'active' || nextValue === 'hidden') {
                            onChange(nextValue);
                        }
                    }}
                >
                    <DropdownMenuRadioItem value="active">
                        <span className="flex w-full items-center justify-between gap-l">
                            <span>{activeLabel}</span>
                            <span className="tabular-nums text-gray-500">{activeCount}</span>
                        </span>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="hidden">
                        <span className="flex w-full items-center justify-between gap-l">
                            <span>{hiddenLabel}</span>
                            <span className="tabular-nums text-gray-500">{hiddenCount}</span>
                        </span>
                    </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
